import { ApiClientError } from '@/lib/api/client/errors'
import { CLIENT_ID_HEADER, getClientId } from '@/lib/api/client-id'
import { CLIENT_INFO_HEADER, getClientInfoHeader } from '@/lib/api/client-info'
import type {
  AnyApiRouteContract,
  ApiSchema,
  ContractBodyInput,
  ContractHeadersInput,
  ContractJsonResponse,
  ContractParamsInput,
  ContractQueryInput,
  EmptySchemaOutput,
} from '@/lib/api/contracts'

// Tuple-wrapped to suppress distributive conditionals: when `Value` is a
// union (e.g. a discriminated union body), naked `Value extends undefined`
// distributes and produces `{ body: A } | { body: B }` instead of
// `{ body: A | B }`. The `[Value] extends [undefined]` form preserves the
// union as-is. See request.test.ts for repro and rationale.
type MaybeField<Key extends string, Value> = [Value] extends [undefined]
  ? { [K in Key]?: never }
  : { [K in Key]: Value }

export type ApiClientRequest<C extends AnyApiRouteContract> = MaybeField<
  'params',
  ContractParamsInput<C>
> &
  MaybeField<'query', ContractQueryInput<C>> &
  MaybeField<'body', ContractBodyInput<C>> &
  MaybeField<'headers', ContractHeadersInput<C>> & {
    signal?: AbortSignal
  }

export interface ApiRawRequestOptions {
  cache?: RequestCache
  headers?: Record<string, string>
}

function replacePathParams(path: string, params: unknown): string {
  if (!params || typeof params !== 'object') return path

  const values = params as Record<string, unknown>
  return path.replace(
    /\[\[?(\.\.\.)?([^\][]+)\]\]?/g,
    (match, rest: string | undefined, key: string) => {
      const value = values[key]
      const isOptionalCatchAll = match.startsWith('[[...')

      if (rest && Array.isArray(value)) {
        return value.map((item) => encodeURIComponent(String(item))).join('/')
      }

      if (value === undefined && isOptionalCatchAll) return ''

      if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
        throw new Error(`Missing route param "${key}"`)
      }

      return encodeURIComponent(String(value))
    }
  )
}

function appendQuery(path: string, query: unknown): string {
  if (!query || typeof query !== 'object') return path

  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue

    if (Array.isArray(value)) {
      // An array of objects (e.g. a SortSpec) is not repeat-append-able — each
      // item would stringify to "[object Object]" and silently corrupt the
      // request (the knowledge tagFilters bug). Encode the WHOLE array as one
      // JSON string param, mirroring how plain objects are sent below; the
      // server-side contract decodes it. Scalar arrays keep repeat-append.
      if (value.some((item) => item !== null && typeof item === 'object')) {
        searchParams.set(key, JSON.stringify(value))
        continue
      }
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue
        searchParams.append(key, String(item))
      }
      continue
    }

    if (typeof value === 'object') {
      searchParams.set(key, JSON.stringify(value))
      continue
    }

    searchParams.set(key, String(value))
  }

  const queryString = searchParams.toString()
  if (!queryString) return path

  return `${path}${path.includes('?') ? '&' : '?'}${queryString}`
}

function buildHeaders(headers: unknown, hasBody: boolean): Record<string, string> {
  const output: Record<string, string> = {}

  if (hasBody) {
    output['Content-Type'] = 'application/json'
  }

  /** Set here rather than per call site so every request carries it without a decision to get wrong. */
  const clientId = getClientId()
  if (clientId) output[CLIENT_ID_HEADER] = clientId

  const clientInfo = getClientInfoHeader()
  if (clientInfo) output[CLIENT_INFO_HEADER] = clientInfo

  if (headers && typeof headers === 'object') {
    for (const [key, value] of Object.entries(headers as Record<string, unknown>)) {
      if (typeof value === 'string') output[key] = value
    }
  }

  return output
}

function parseOptionalSchema<S extends ApiSchema | undefined>(
  schema: S,
  value: unknown
): EmptySchemaOutput<S> {
  if (!schema) return undefined as EmptySchemaOutput<S>
  return schema.parse(value) as EmptySchemaOutput<S>
}

async function readResponseBody(response: Response): Promise<{ parsed: unknown; raw?: string }> {
  const text = await response.text()
  if (!text) return { parsed: undefined }

  try {
    return { parsed: JSON.parse(text) as unknown, raw: text }
  } catch {
    return { parsed: text, raw: text }
  }
}

function messageFromErrorBody(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>
    const message = record.message ?? record.error
    if (typeof message === 'string' && message.length > 0) return message
  }

  return fallback
}

function codeFromErrorBody(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>
    if (typeof record.code === 'string' && record.code.length > 0) return record.code
  }
  return undefined
}

function isSchemaValidationError(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'issues' in error &&
      Array.isArray((error as { issues?: unknown }).issues)
  )
}

/**
 * Compresses a ZodError's issues into a short, readable "field: reason" summary
 * so a failed response tells you which field was wrong instead of a bare
 * "Response failed contract validation".
 */
function summarizeSchemaIssues(error: unknown): string {
  const issues = (error as { issues?: unknown }).issues
  if (!Array.isArray(issues)) return ''
  const summary = issues
    .slice(0, 3)
    .map((issue) => {
      const path = Array.isArray(issue?.path) ? issue.path.join('.') : ''
      const message = typeof issue?.message === 'string' ? issue.message : 'invalid'
      return path ? `${path}: ${message}` : message
    })
    .join('; ')
  const extra = issues.length > 3 ? ` (+${issues.length - 3} more)` : ''
  return summary ? `${summary}${extra}` : ''
}

export async function requestJson<C extends AnyApiRouteContract>(
  contract: C,
  input: ApiClientRequest<C>
): Promise<ContractJsonResponse<C>> {
  if (contract.response.mode !== 'json') {
    throw new Error(`Contract ${contract.method} ${contract.path} does not declare a JSON response`)
  }

  const parsedBody = parseOptionalSchema(contract.body, input.body)
  const parsedHeaders = parseOptionalSchema(contract.headers, input.headers)

  const url = contractUrl(contract, input)
  const hasBody = parsedBody !== undefined && contract.method !== 'GET'

  const response = await fetch(url, {
    method: contract.method,
    headers: buildHeaders(parsedHeaders, hasBody),
    body: hasBody ? JSON.stringify(parsedBody) : undefined,
    signal: input.signal,
  })

  const { parsed, raw } = await readResponseBody(response)
  if (!response.ok) {
    throw new ApiClientError({
      status: response.status,
      message: messageFromErrorBody(parsed, `Request failed with ${response.status}`),
      body: parsed,
      rawBody: raw,
      code: codeFromErrorBody(parsed),
    })
  }

  try {
    return contract.response.schema.parse(parsed) as ContractJsonResponse<C>
  } catch (error) {
    if (isSchemaValidationError(error)) {
      const details = summarizeSchemaIssues(error)
      throw new ApiClientError({
        status: response.status,
        message: details
          ? `Response failed contract validation — ${details}`
          : 'Response failed contract validation',
        body: parsed,
        rawBody: raw,
      })
    }
    throw error
  }
}

/**
 * The URL a contract resolves to for `input`, validated the same way a request
 * would be. For a download the browser should stream itself — an anchor
 * navigation rather than a fetch that buffers the body — so the caller needs
 * the address, not the response.
 */
export function contractUrl<C extends AnyApiRouteContract>(
  contract: C,
  input: ApiClientRequest<C>
): string {
  const parsedParams = parseOptionalSchema(contract.params, input.params)
  const parsedQuery = parseOptionalSchema(contract.query, input.query)
  return appendQuery(replacePathParams(contract.path, parsedParams), parsedQuery)
}

export async function requestRaw<C extends AnyApiRouteContract>(
  contract: C,
  input: ApiClientRequest<C>,
  options: ApiRawRequestOptions = {}
): Promise<Response> {
  const parsedBody = parseOptionalSchema(contract.body, input.body)
  const parsedHeaders = parseOptionalSchema(contract.headers, input.headers)

  const url = contractUrl(contract, input)
  const hasBody = parsedBody !== undefined && contract.method !== 'GET'
  const headers = {
    ...buildHeaders(parsedHeaders, hasBody),
    ...options.headers,
  }

  const response = await fetch(url, {
    method: contract.method,
    headers,
    body: hasBody ? JSON.stringify(parsedBody) : undefined,
    signal: input.signal,
    cache: options.cache,
  })

  if (!response.ok) {
    const { parsed, raw } = await readResponseBody(response)
    throw new ApiClientError({
      status: response.status,
      message: messageFromErrorBody(parsed, `Request failed with ${response.status}`),
      body: parsed,
      rawBody: raw,
    })
  }

  return response
}
